import Loading from '@components/v2/ui/Loading/Loading';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import SearchableMenuList from '@components/v2/ui/Menu/SearchableMenuList';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import * as R from 'ramda';
import { memo, useCallback, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useOrderBookStore from '../../../../../stores/OrderBook/orderBook.store';
import { OrderProductsTestIds } from '../../../../../utils/testIds/OrderProducts.testIds';
import { ESProductColor } from '../../../../products/ProductTypes';
import { productSearchRepository } from '../../../../products/repositories/productSearch/productSearch.repository';
import { getColorStyles, useOrderProductStyles } from '../styles/useOrderProductStyles';

type OrderProductColorMenuProps = {
  productId?: string;
  anchorEl: null | HTMLElement;
  currentColorId: string;
  onClick: (onClick: ESProductColor) => void;
  updatePosition: () => void;
};

const OrderProductColorMenu = ({
  anchorEl,
  productId,
  currentColorId,
  onClick,
  updatePosition,
}: OrderProductColorMenuProps) => {
  const { classes } = useOrderProductStyles();
  const { selectedOrderCustomerId } = useOrderBookStore(
    useShallow((state) => ({
      selectedOrderCustomerId: state.selectedOrder?.customer?.id,
    })),
  );

  const [isLoadingColors, setIsLoadingColors] = useState(false);
  const [colors, setColors] = useState<Array<ESProductColor>>([]);

  const getProductColors = useCallback(async () => {
    if (!productId) {
      return;
    }

    setIsLoadingColors(true);
    const response = await productSearchRepository().getProductSearchColors(
      productId,
      selectedOrderCustomerId,
    );
    setIsLoadingColors(false);
    if (response.ok && response.data) {
      setColors(response.data as unknown as ESProductColor[]);
    }
  }, [productId, selectedOrderCustomerId]);

  useEffect(() => {
    if (!R.isNil(anchorEl)) {
      getProductColors();
    }
  }, [anchorEl, getProductColors]);

  useEffect(() => {
    if (!R.isNil(anchorEl) && !isLoadingColors) {
      updatePosition();
    }
  }, [anchorEl, isLoadingColors, updatePosition]);

  const sortedColors = [...colors].sort(
    (first, second) => Number(second.id === currentColorId) - Number(first.id === currentColorId),
  );

  if (isLoadingColors) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 24,
        }}
      >
        <Loading size={24} sx={{ color: 'inherit' }} />
      </Box>
    );
  }

  return (
    <SearchableMenuList
      items={sortedColors}
      testId={OrderProductsTestIds.COLOR_SEARCH}
      item={{
        getKey: (color) => color.id,
        getSearchValue: (color) => `${color.code} ${color.name}`,
        onSelect: onClick,
        render: (color) => (
          <MenuItem selected={currentColorId === color.id} onClick={() => onClick(color)}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: 1,
                minWidth: 0,
                width: '100%',
              }}
            >
              <div className={classes.colorBadge} style={getColorStyles(color)} />
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                {color.code}
              </Typography>
              <Typography noWrap variant="body2" title={color.name} sx={{ minWidth: 0 }}>
                {color.name}
              </Typography>
            </Box>
          </MenuItem>
        ),
      }}
    />
  );
};

export default memo(OrderProductColorMenu);
